import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, EDIT } from "const"
import { CcRequirementSmartCompliance } from "server/models"
import { StateType, deepCopy, hasAccess, hasProjectAccess } from "client/utils"
import {
    PRM_ACCOUNT_SETTINGS,
    PRM_PROJECT_SETTINGS,
    StoreType,
    CcRequirementSmartComplianceList,

    LoadCcRequirementSmartCompliances,
    SortCcRequirementSmartCompliance,
    DeleteCcRequirementSmartCompliance,

    setCcRequirementSmartComplianceList,
    setCcRequirementSmartComplianceIds,
    setCcRequirementSmartComplianceSort,
    addModal,
} from "client/store"
import {
    Container,
    Loader,
    Button,
    Table,
    SortRows,
    DeleteRow,
} from "client/components"

import Columns from "./columns"
import Edit from "./edit"

const SmartCompliance = () => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const complianceList = useSelector((state: StoreType) => state.ccRequirementSmartCompliance.complianceList)
    const complianceIds = useSelector((state: StoreType) => state.ccRequirementSmartCompliance.complianceIds)
    const sortColumn = useSelector((state: StoreType) => state.ccRequirementSmartCompliance.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.ccRequirementSmartCompliance.sortOrder)
    const filters = useSelector((state: StoreType) => state.ccRequirementSmartCompliance.filters)
    const reloadList = useSelector((state: StoreType) => state.ccRequirementSmartCompliance.reloadList)

    const accountSettings = useSelector((state: StoreType) => state.system.accountSettings)
    const projectSettings = useSelector((state: StoreType) => state.project.projectSettings)
    const { defaultRequirementsSmartComplianceAssigned } = project.id ? projectSettings.cc : accountSettings.cc

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        LoadCcRequirementSmartCompliances(userData.accountId, project.id).then((res) => {
            dispatch(setCcRequirementSmartComplianceList(res))
            setLoadState(IDLE)
        })
    }, [reloadList, project, defaultRequirementsSmartComplianceAssigned])

    useEffect(() => {
        const sortedIds = SortCcRequirementSmartCompliance(Object.values(complianceList), sortColumn, sortOrder, filters)
        dispatch(setCcRequirementSmartComplianceIds(sortedIds))
    }, [complianceList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof CcRequirementSmartCompliance, order) => {
        dispatch(setCcRequirementSmartComplianceSort(column, order))
    }

    const onDelete: DeleteRow = (compliance: CcRequirementSmartCompliance, callback) => {
        DeleteCcRequirementSmartCompliance(compliance.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const complianceListCopy: CcRequirementSmartComplianceList = complianceList ? deepCopy(complianceList) : {}
                delete complianceListCopy[compliance.id]
                dispatch(setCcRequirementSmartComplianceList(complianceListCopy))
                dispatch(setCcRequirementSmartComplianceIds(complianceIds.filter((id) => id !== compliance.id)))
            })
        })
    }

    const editAccess = !project.id
        ? hasAccess(PRM_ACCOUNT_SETTINGS, EDIT)
        : hasProjectAccess(PRM_PROJECT_SETTINGS, EDIT)

    const columns = Columns(editAccess)
    return (
        <Container>
            {
                loadState === PENDING
                && <Loader />
            }
            <Table
                columns={columns}
                dataList={complianceList}
                dataIds={complianceIds}
                sortColumn={sortColumn}
                sortOrder={sortOrder}
                rowSize="sm"
                showPagination
                onSort={onSort}
                onDelete={onDelete}
                bottomContent={editAccess && (
                    <Button
                        variant="success"
                        onClick={() => dispatch(addModal({
                            key: "addSmartCompliance",
                            content: <Edit complianceId={0} editAccess={editAccess} />,
                        }))}
                    >
                        {lang.add}
                    </Button>
                )}
            />
        </Container>
    )
}
export default SmartCompliance
